import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import DynamicInput from 'dashboard/components/DynamicInput';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned';
import TransactionTypeStatusSticker from 'dashboard/components/TransactionTypeStatusSticker';
import { Header } from 'dashboard/layouts/IndexViewLayoutV2';
import { TransactionTypeStatus } from 'dashboard/models/transaction-type';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Permission } from 'lib/permissions';

const StyledStickerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${Spacing[2]};
`;

interface DashboardTransactionTypesActiveFieldsHeaderProps {
  name: string;
  transactionId: string;
  status: TransactionTypeStatus;
  disabled: boolean;
  showSaveButton?: boolean;
  unsavedChanges?: boolean;
  setName: (name: string) => void;
  saveTransactionType: () => void;
  openDeactivateTransactionTypeModal: () => void;
  handleActivateTransactionType: () => void;
}

const DashboardTransactionTypesActiveFieldsHeader = ({
  name,
  transactionId,
  status,
  disabled,
  showSaveButton,
  unsavedChanges,
  setName,
  saveTransactionType,
  openDeactivateTransactionTypeModal,
  handleActivateTransactionType,
}: DashboardTransactionTypesActiveFieldsHeaderProps) => {
  const menuItems = [
    <DropdownMenu.Item
      key='transaction-type-show__statuses'
      as={Link}
      to={`/dashboard/transaction-types/${transactionId}/statuses`}
    >
      Custom Statuses
    </DropdownMenu.Item>,
    <DropdownMenu.Item
      key='transaction-type-show__inactive-fields'
      as={Link}
      to={`/dashboard/transaction-types/${transactionId}/inactive-fields`}
    >
      Inactive Fields
    </DropdownMenu.Item>,
    <FeatureFlagged
      featureFlag='global:enable-platform-connection-dashboard'
      key='transaction-type-show__platform-connections'
    >
      <DropdownMenu.Item as={Link} to={`/dashboard/platform-connections/${transactionId}`}>
        Connections
      </DropdownMenu.Item>
    </FeatureFlagged>,
    ...(status === TransactionTypeStatus.Active
      ? [
          <DropdownMenu.Item
            id='transaction-type-show__deactivate'
            key='transaction-type-show__deactivate'
            variant='danger'
            onClick={openDeactivateTransactionTypeModal}
          >
            Deactivate
          </DropdownMenu.Item>,
        ]
      : []),
    ...(status === TransactionTypeStatus.Inactive
      ? [
          <DropdownMenu.Item
            id='transaction-type-show__activate'
            key='transaction-type-show__activate'
            variant='basic'
            onClick={handleActivateTransactionType}
          >
            Activate
          </DropdownMenu.Item>,
        ]
      : []),
  ];

  return (
    <Header
      prepend={<NavBack to='/dashboard/transaction-types' />}
      append={
        unsavedChanges ? (
          <StyledStickerContainer>
            <TransactionTypeStatusSticker status={status} />
            <Sticker variant='yellow'>Unsaved</Sticker>
          </StyledStickerContainer>
        ) : (
          <TransactionTypeStatusSticker status={status} />
        )
      }
      title={
        <DynamicInput
          disabled={status === TransactionTypeStatus.Inactive}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      }
    >
      <Permissioned permission={Permission.TransactionTypeWrite} fallback={null}>
        <HStack spacing={Spacing[2]}>
          {showSaveButton && (
            <Button
              disabled={disabled}
              id='transaction-type-show__save-button'
              size='md'
              variant='primary'
              onClick={saveTransactionType}
            >
              Save
            </Button>
          )}

          <DropdownMenu
            trigger={
              <Button
                aria-label='More actions'
                id='transaction-type-show__button--dropdown'
                size='md'
                variant='basic'
                width='icon'
              >
                <MoreIcon />
              </Button>
            }
          >
            {menuItems}
          </DropdownMenu>
        </HStack>
      </Permissioned>
    </Header>
  );
};

export default DashboardTransactionTypesActiveFieldsHeader;
